import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Table, Tooltip } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { DownOutlined } from "@ant-design/icons";
import ImagesComponent from "../Images/Images";
import allAboardLogo from "../../assets/images/profile.jpg";
import { useDispatch, useSelector } from "react-redux";
import { debounce, useGetApiRequests, useNavigate, initLoadData, employeeFilterApiCall, handleExpand, searchEmployeeByName, storeData } from "../../common/CommonExport";
import CustomFilterDropdown from "./CustomFilterDropdown";
import { setDeprovision } from "../../redux/slices/updateProfileSlice";
import { setAppraisalTab } from "../../redux/slices/updateProfileSlice";
import PopupModal from "../../pages/employee/employee-details/employee-profile/modal/PopupModal";
import { setEmployeePageLimit, setEmployeePageStart } from "../../redux/slices/employeePaginationSlice";
import { useIsMobile } from "../../hooks/commonHooks";
import { CustomExpandIcon, FilterIcon, getRowClassName, handelExpand } from "./helper/EmployeeTableHelper";
import StepTracker from "./StepTracker/StepTracker";
import { setFilterData, setSearchTerm } from "../../redux/slices/tableFilterSlice";
import { setCsvFilterData } from "../../redux/slices/userDasboardSlice";
import { AllFilterValues, DocumentName, EmployeeData, EmployeeTableProps, ExpandedData, FilterData, RootState } from "./EmployeeTableTypes";



const EmployeeTable: React.FC<EmployeeTableProps> = (props) => {
  const employeePaginationStart = useSelector((state: RootState) => state?.employeePageSlice.start);
  const employeePaginationLimit = useSelector((state: RootState) => state?.employeePageSlice.limit);

  const { dashboardData } = props;
  const imageBaseUrl = process.env.REACT_APP_IMAGE_VIEW;
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const tableReload = useSelector((state: RootState) => state?.tableFilter?.filterValue);
  const tableFilterState = useSelector((state: RootState) => state.tableFilter);
  const [localSearch, setLocalSearch] = useState(tableFilterState.searchTerm || "");
  const EmployeeFilter = useGetApiRequests("dashboard", "GET");
  const documentStatus = useGetApiRequests("documentStatus", "GET");
  const [isExpand, setIsExpand] = useState(false);
  const [employeeData, setEmployeeData] = useState<EmployeeData[]>([]);
  const [employeeDataSearch, setEmployeeDataSearch] = useState<EmployeeData[]>([]);

  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<ExpandedData | null>(null);
  const [allFilterValues, setAllFilterValues] = useState<AllFilterValues>({});
  const currentPage = employeePaginationStart;
  const pageSize = employeePaginationLimit;
  const [loader, setLoader] = useState<boolean>(false);
  const [modalErrMessage, setModalErrMessage] = useState("");
  const [popModal, setPopModal] = useState(false);
  const [modalResponse, setModalResponse] = useState("");
  const [modalButtonShow, setmodalButtonShow] = useState(true);
  const navigate = useNavigate();
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: isExpand ? 10 : pageSize,
    current: currentPage,
    total: employeeDataSearch.length,
    onChange: (page: number, pageSize: number) => {
      dispatch(setEmployeePageStart(page));
      dispatch(setEmployeePageLimit(pageSize));
    },
  });

  const handelProfile = useCallback(
    (record: React.Key | undefined, employeeTypeId: string, employeeName: string) => {
      navigate(`/employee/details/${record}?employeeTypeId=${employeeTypeId}&employeeName=${employeeName}`);
      storeData("employeeId", record, true);
      dispatch(setDeprovision(false));
      dispatch(setAppraisalTab(false));
    },
    [navigate],
  );

  const debouncedFilter = useMemo(
    () =>
      debounce((value: string) => {
        applyFiltersAndSearch(value, tableFilterState.filterData); // use combined filter + search
        dispatch(setSearchTerm(value));
      }, 500),
    [employeeData, tableFilterState.filterData],
  );

  const applyFiltersAndSearch = (searchTerm: string, filterData: FilterData) => {
    let filtered = [...employeeData];

    if (filterData.designationNames?.length) filtered = filtered.filter((emp) => filterData.designationNames!.includes(emp.designation ?? ""));
    if (filterData.gradeNames?.length) filtered = filtered.filter((emp) => filterData.gradeNames!.includes(emp.grade ?? ""));
    if (filterData.departmentNames?.length) filtered = filtered.filter((emp) => filterData.departmentNames!.includes(emp.dept ?? ""));
    if (filterData.reportingToNames?.length) filtered = filtered.filter((emp) => filterData.reportingToNames!.includes(emp.reportingTo ?? ""));


    if (searchTerm) {
      filtered = searchEmployeeByName(filtered, searchTerm);
    }

    setEmployeeDataSearch(filtered);
    dispatch(setCsvFilterData(filtered));
  };

  const handleClosePopUpModal = () => {
    setPopModal(false);
  };

  useEffect(() => {
    if (tableReload) {
      employeeFilterApiCall(
        tableReload,
        EmployeeFilter,
        setAllFilterValues,
        setEmployeeData,
        setEmployeeDataSearch,
        setLoader,
        setPopModal,
        setmodalButtonShow,
        setModalErrMessage,
        setModalResponse,
        handleClosePopUpModal,
      );
    }
  }, [tableReload]);

  useEffect(() => {
    applyFiltersAndSearch(localSearch, tableFilterState.filterData);
  }, [tableFilterState, employeeData, localSearch]);

  useEffect(() => {
    if (dashboardData) {
      initLoadData(dashboardData, setAllFilterValues, setEmployeeData, setEmployeeDataSearch, dispatch);
    }
  }, [dashboardData, props.reLoadPage]);

  const columns: ColumnsType<EmployeeData> = useMemo(() => {
    return [
      {
        title: " Employee Name",
        dataIndex: "employeename",
        key: "employeename",
        width: "21%",
        sorter: (a, b) => (a.employeename ?? "").localeCompare(b.employeename ?? ""),
        filterIcon: <DownOutlined />,
        render: (text: string, record: EmployeeData) => {
          return (
            <div className="employee-image flex items-center justify-start">
              <div className="employee-image_container">
                <ImagesComponent alt="logo" src={record?.profileUrl ? `${imageBaseUrl}${record?.profileUrl}` : allAboardLogo} className="employee_image" />
              </div>
              <p className="cursor-pointer " onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
                {text}
              </p>
            </div>
          );
        },
      },
      {
        title: "DOJ",
        dataIndex: "doj",
        key: "doj",
        sorter: (a, b) => {
          const dateA = new Date(a.doj.split("-").reverse().join("-")).getTime();
          const dateB = new Date(b.doj.split("-").reverse().join("-")).getTime();
          return dateA - dateB;
        },
        width: "8%",
        render: (text, record) => (
          <p className="cursor-pointer" onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
            {text ?? "-"}
          </p>
        ),
      },
      {
        title: "Grade",
        dataIndex: "grade",
        filters: allFilterValues?.grades,
        filteredValue: tableFilterState.filterData.gradeNames || null,
        onFilter: (value: boolean | React.Key, record: EmployeeData) => {
          return record.grade === value;
        },
        filterSearch: true,
        width: "13%",
        filterIcon: (filtered: boolean) => FilterIcon({ filtered, columnTitle: "Grade" }),
        render: (text, record) => (
          <p className="cursor-pointer" onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
            {text ?? "-"}
          </p>
        ),
      },
      {
        title: "Dept",
        dataIndex: "dept",
        filters: allFilterValues?.dept,
        filteredValue: tableFilterState.filterData.departmentNames || null,
        onFilter: (value: boolean | React.Key, record: EmployeeData) => {
          return record.dept === value;
        },
        filterSearch: true,
        width: "15%",
        filterIcon: (filtered: boolean) => FilterIcon({ filtered, columnTitle: "Dept" }),
        render: (text, record) => (
          <p className="cursor-pointer custom-cell-class-department" onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
            {text ?? "-"}
          </p>
        ),
      },
      {
        title: "Designation",
        dataIndex: "designation",
        filters: allFilterValues?.designation,

        filteredValue: tableFilterState.filterData.designationNames || null,

        onFilter: (value: boolean | React.Key, record: EmployeeData) => {
          return record.designation === value;
        },
        filterSearch: true,
        width: "15%",
        filterIcon: (filtered: boolean) => FilterIcon({ filtered, columnTitle: "Designation" }),
        render: (text, record) => (
          !isExpand && text?.length > 20 ? (
            <Tooltip title={text}>
              <p className="cursor-pointer custom-cell-class" onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
                {text ?? "-"}
              </p>
            </Tooltip>
          ) : (
            <p className="cursor-pointer custom-cell-class" onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
              {text ?? "-"}
            </p>
          )
        ),
      },

      {
        title: "Reporting to",
        dataIndex: "reportingTo",
        filters: allFilterValues?.reportingTo,
        filteredValue: tableFilterState.filterData.reportingToNames || null,

        onFilter: (value: boolean | React.Key, record: EmployeeData) => {
          const isMatch = record.reportingTo === value;
          return isMatch;
        },
        filterSearch: true,
        width: "15%",
        filterIcon: (filtered: boolean) => FilterIcon({ filtered, columnTitle: "Reporting to" }),
        render: (text, record) => (
          <p className="cursor-pointer" onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
            {text ?? "-"}
          </p>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        filters: allFilterValues?.status,
        filterDropdown: ({ filters, selectedKeys, confirm }) => (
          <CustomFilterDropdown
            setLoader={setLoader}
            filters={filters}
            confirm={confirm}
            EmployeeFilter={EmployeeFilter}
            setAllFilterValues={setAllFilterValues}
            setEmployeeData={setEmployeeData}
            setEmployeeDataSearch={setEmployeeDataSearch}
            reLoadPage={props?.reLoadPage}
            setPopModal={setPopModal}
            setmodalButtonShow={setmodalButtonShow}
            setModalErrMessage={setModalErrMessage}
            setModalResponse={setModalResponse}
            handleClosePopUpModal={handleClosePopUpModal}
          />
        ),
        filterSearch: true,
        width: "13%",
        filterIcon: (filtered: boolean) => FilterIcon({ filtered, columnTitle: "Status" }),
        render: (text, record) => {
          return (
            <div className="employee-image flex items-center justify-start">
              <p className="cursor-pointer" onClick={() => handelProfile(record?.key, record?.employeeTypeId, record?.employeename ?? "")}>
                {text ?? "-"}
              </p>
            </div>
          );
        },
      },
    ];
  }, [allFilterValues?.grades, allFilterValues?.dept, allFilterValues?.designation, allFilterValues?.reportingTo, allFilterValues?.status, handelProfile, EmployeeFilter, isExpand]);
  const expandedRowRender = (record: EmployeeData) => {
    if (record?.key === expandedRowKey) {
      return (
        <>
          {expandedData?.data?.documentNames.map((stepper: DocumentName, index: number) => {
            const descriptions: string[] = [];
            const currentStatus = expandedData?.data?.employeeFootPrintDto;
            let result = 0;
            for (const item of currentStatus) {
              if (item?.discriminator?.toLowerCase().includes(stepper?.name?.toLowerCase()) || item?.description === "Employee information saved") descriptions.push(item.description);
            }
            if (descriptions.includes("Document signed off")) result = 3;
            else if (descriptions.includes("Document sent")) result = 2;
            else if (descriptions.includes("Employee information saved")) result = 1;
            return <StepTracker key={`${stepper?.templateCategoryId}`} currentData={stepper} currentStep={result} record={record} />;
          })}
        </>
      );
    }
    return null;
  };
  const setCurrent = (e: TablePaginationConfig) => {
    setPaginationConfig({
      ...paginationConfig,
      current: e.current || 1,
      total: e.total || 0,
      pageSize: e.pageSize || 10,
    });
  };
  return (
    <div className="employee-table z-[1] responsive">
      {/* {loader && <Loader />} */}
      <div className="birth-anniversary__tab flex justify-between relative ">
        <div className="card-title justify-start flex">
          <h4>Employees</h4>
        </div>
        <div className="flex justify-end relative top-[-9px]">
          <div className="search-field pr-[10px]">
            <div className="search-filter mr-4">
              <Input
                placeholder="Search"
                className="search"
                value={localSearch}
                onChange={(e) => {
                  setLocalSearch(e.target.value);
                  setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  debouncedFilter(e.target.value);
                }}
              />
              <span className="material-symbols-outlined"> search </span>
            </div>
          </div>
          <div className="expand expand-icon expand-arrow-icon justify-end text-primary cursor-pointer mt-1">
            <span className="material-symbols-outlined text-lg" onClick={() => handelExpand(dispatch, setIsExpand, setPaginationConfig)}>
              open_in_full
            </span>
          </div>
        </div>
      </div>
      <Table
        scroll={{ x: true }}
        loading={loader}
        columns={columns}
        expandable={
          isMobile
            ? undefined
            : {
              expandedRowRender,
              defaultExpandedRowKeys: ["0"],
              expandIcon: CustomExpandIcon,
              onExpand: (expanded, record) => {
                if (expanded) handleExpand(record, documentStatus, (data) => setExpandedData(data as ExpandedData), setExpandedRowKey, expandedRowKey, setLoader);
              },
            }
        }
        dataSource={employeeDataSearch}
        pagination={paginationConfig}
        rowClassName={getRowClassName}
        onChange={(pagination, filters, sorter) => {
          dispatch(
            setFilterData({
              designationNames: filters.designation ? filters.designation.map(String) : [],
              gradeNames: filters.grade ? filters.grade.map(String) : [],
              departmentNames: filters.dept ? filters.dept.map(String) : [],
              reportingToNames: filters.reportingTo ? filters.reportingTo.map(String) : [],
            }),
          );

          if (filters.designation) {
            dispatch(setFilterData({ designationNames: filters.designation.map(String) }));
          }
          if (filters.grade) {
            dispatch(setFilterData({ gradeNames: filters.grade.map(String) }));
          }
          if (filters.dept) {
            dispatch(setFilterData({ departmentNames: filters.dept.map(String) }));
          }
          if (filters.reportingTo) {
            dispatch(
              setFilterData({
                reportingToNames: filters.reportingTo.map(String),
              }),
            );
          }
          setCurrent(pagination);
          const employeeTags = document.getElementsByClassName("ant-table-content") as HTMLCollectionOf<HTMLElement>;
          if (employeeTags.length > 0) employeeTags[0].scrollTop = 0;
        }}
      />
      {popModal && <PopupModal message={modalErrMessage} open={popModal} handleModalClose={handleClosePopUpModal} response={modalResponse} modalButtonShow={modalButtonShow} />}
    </div>
  );
};
export default EmployeeTable;
